"use strict";

import { ObjectCreator } from "../objectCreator.js";
import { Application } from "../../main/application.js";
import { Screen } from "../../systems/core/screen.js";
import { AssetImporter } from "../../systems/core/assetImporter.js";
import { TransformHandler } from "../transformHandler.js";

export class TilingSprite extends PIXI.TilingSprite {
    constructor(config) {
        // Create the main Object
        let texture = undefined;

        if (config.textureID !== undefined) {
            if (Array.isArray(config.textureID)) {
                texture = AssetImporter.sheetFind(config.textureID[0], config.textureID[1]);
            } else {
                texture = AssetImporter.resourcesFind(config.textureID).texture;
            }
        } else if (config.texture !== undefined) {
            texture = config.texture;
        } else {
            console.error("Sprite can't exist without a texture.");
        }

        super(texture, 0, 0);
        this.__objectType = "GameObject";

        this.name = config.name;
        
        // Create Parenting
        ObjectCreator.createParenting(this, config);

        // Create Anchoring
        ObjectCreator.createAnchoring(this, true, false, config);
        this._refreshPivot();

        // Set Flipping
        ObjectCreator.setFlipping(this, config);

        // Set Original Scale/Size
        this._originalScale = 1;
        this._originalSize = { width: this.width, height: this.height };
        
        // Set Scale
        ObjectCreator.setScale(this, config);
        // Set Size
        ObjectCreator.setSize(this, config);
        this._refreshScale();

        // Set Position
        ObjectCreator.setPosition(this, false, config);
        this._refreshPosition();

        // Set Rotation Angle
        ObjectCreator.setRotationAngle(this, config);
        // Set Visibility
        ObjectCreator.setVisibility(this, config);
        // Set Renderability
        ObjectCreator.setRenderability(this, config);

        ObjectCreator.setResolutionListeners(this, false, config);
        ObjectCreator.setOrientationListeners(this, false, config);

        ObjectCreator.setDisposeCallbacks(this);

        // Set TileScale
        if (config.tileScale !== undefined) {
            this.tileScale.set(config.tileScale.x, config.tileScale.y);
        }

        // Set TilePosition
        if (config.tilePosition !== undefined) {
            this.tilePosition.set(config.tilePosition.x, config.tilePosition.y);
        }

        if (config.worldPosition !== undefined) {
            this.setWorldPosition(config.worldPosition);
        }

        this._sizeRatio = this.width / this.height;

        this._externalBounds = config.externalBounds;
        this._useBounds = { width: 0, height: 0 };
        
        if (config.stretchToFullscreen) {
            this.stretchToFullscreen = true;
            this.onResolutionChanged();
        } else if (config.keepFullscreen) {
            this.keepFullscreen = true;
            this.onResolutionChanged();
        } else if (config.stretchOnX) {
            this.stretchOnX = true;
            this.preserveAspectRatio = config.preserveAspectRatio;
            this.onResolutionChanged();
        } else if (config.stretchOnY) {
            this.stretchOnY = true;
            this.preserveAspectRatio = config.preserveAspectRatio;
            this.onResolutionChanged();
        }

        config.onInitialize?.bind(this)(config);
    }

    dispose() {
        this._onDispose.dispatch();
        if (this.parentContainer !== undefined) {
            this.parentContainer.removeChild(this);
        }
        this.destroy();
    }

    // SetParent
    setParent(newParent) {
        this.parentContainer = newParent;
        this.parentContainer.addChild(this);
        this.onResolutionChanged();
    }

    // Get - Set X/Y
    getX() {
        return this.x;
    }
    getY() {
        return this.y;
    }

    setX(x) {
        this.anchoring.margin.x = x;
        this._refreshPosition();
    }
    setY(y) {
        this.anchoring.margin.y = y;
        this._refreshPosition();
    }

    // Get - Set Position
    getPosition() {
        return { x: this.x, y: this.y };
    }
    setPosition(x,y) {
        this.anchoring.margin.x = x.x ?? x;
        this.anchoring.margin.y = x.y ?? y;
        this._refreshPosition();
    }

    getRealWorldPosition() {
        this.parentContainer.updateTransform();
        return this.parentContainer.toGlobal(this.getPosition());
    }
    getWorldPosition() {
        let position = this.getRealWorldPosition();
        position.x -= Application.main.worldCenterX;
        position.y -= Application.main.worldCenterY;
        return position;
    }
    setRealWorldPosition(x,y) {
        this._tPosition.x = x.x ?? x;
        this._tPosition.y = x.y ?? y;
        this._tPosition = this.parentContainer.toLocal(this._tPosition);
        
        this.anchoring.margin.x = this._tPosition.x;
        this.anchoring.margin.y = this._tPosition.y;

        this._refreshPosition();
    }
    setWorldPosition(x,y) {
        this._tPosition.x = (x.x ?? x) + Application.main.worldCenterX;
        this._tPosition.y = (x.y ?? y) + Application.main.worldCenterY;
        this._tPosition = this.parentContainer.toLocal(this._tPosition);
        
        this.anchoring.margin.x = this._tPosition.x;
        this.anchoring.margin.y = this._tPosition.y;

        this._refreshPosition();
    }

    getWorldPositionScale() {
        return 1;
    }
    transformLocalToWorld(value) {
        return value / this.getWorldPositionScale();
    }
    transformLocalToWorldPosition(position) {
        position.x = this.transformLocalToWorld(position.x);
        position.y = this.transformLocalToWorld(position.y);
    }

    _refreshPosition() {
        if (this.anchoring.anchorBounds !== undefined) {
            TransformHandler.updateAnchor(this);
        } else {
            TransformHandler.updateMargin(this);
        }
    }

    setPivotX(x) {
        this.anchoring.pivot.x = x;
        this._refreshPivot();
    }
    setPivotY(y) {
        this.anchoring.pivot.y = y;
        this._refreshPivot();
    }
    setPivot(x,y) {
        this.anchoring.pivot.x = x;
        this.anchoring.pivot.y = y ?? x;
        this._refreshPivot();
    }

    _refreshPivot() {
        this.anchor.set(this.anchoring.pivot.x, this.anchoring.pivot.y);
    }

    setAnchorX(x) {
        this.anchoring.anchor.x = x;
        this._refreshPosition();
    }
    setAnchorY(y) {
        this.anchoring.anchor.y = y;
        this._refreshPosition();
    }
    setAnchor(x,y) {
        this.anchoring.anchor.x = x.x ?? x;
        this.anchoring.anchor.y = x.y ?? y ?? x;
        this._refreshPosition();
    }

    setAnchorBounds(bounds) {
        this.anchoring.anchorBounds = bounds;
        this._refreshPosition();
    }

    // Get - Set Scale
    getScale() {
        return this.scale;
    }
    setScale(x,y) {
        this.scale.set(x, y ?? x);

        this._refreshScale();
    }
    setScaleX(x) {
        this.scale.set(x, this.scale.y);

        this._refreshScale();
    }
    setScaleY(y) {
        this.scale.set(this.scale.x, y);

        this._refreshScale();
    }

    // Get - Set Width/Height/Size
    getSize() {
        return { width: this.width, height: this.height };
    }
    getRealWorldSize() {
        return {
            width: this.scaledWidth * Screen.main.stageScale,
            height: this.scaledHeight * Screen.main.stageScale
        };
    }

    setWidth(width) {
        this.width = width;
        this._refreshScale();
    }
    setHeight(height) {
        this.height = height;
        this._refreshScale();
    }
    setSize(width,height) {
        if (height === undefined) {
            if (typeof width === "number") {
                height = width;
            } else if (width.x !== undefined) {
                height = width.y;
                width = width.x;
            } else {
                height = width.height;
                width = width.width;
            }
        }
        this.width = width;
        this.height = height;
        this._refreshScale();
    }

    get scaledWidth() {
        return this.width * this.lossyScale;
    }
    get scaledHeight() {
        return this.height * this.lossyScale;
    }
    get unscaledWidth() {
        return this.width / this.lossyScale;
    }
    get unscaledHeight() {
        return this.height / this.lossyScale;
    }

    // Get - Set TilePosition
    getTilePositionX() {
        return this.tilePosition.x;
    }
    getTilePositionY() {
        return this.tilePosition.y;
    }
    getTilePosition() {
        return { x: this.tilePosition.x, y: this.tilePosition.y };
    }

    setTilePositionX(x) {
        this.tilePosition.x = x;
    }
    setTilePositionY(y) {
        this.tilePosition.y = y;
    }
    setTilePosition(x,y) {
        this.tilePosition.set(x, y);
    }

    // Get - Set TileScale
    getTileScaleX() {
        return this.tileScale.x;
    }
    getTileScaleY() {
        return this.tileScale.y;
    }
    getTileScale() {
        return { x: this.tileScale.x, y: this.tileScale.y };
    }

    setTileScaleX(x) {
        this.tileScale.x = x;
    }
    setTileScaleY(y) {
        this.tileScale.y = y;
    }
    setTileScale(x,y) {
        this.tileScale.set(x, y ?? x);
    }

    _refreshScale() {
        this.scale.set(
            Math.abs(this.scale.x) * (this.flipX ? -1 : 1),
            Math.abs(this.scale.y) * (this.flipY ? -1 : 1));
    }

    get lossyScaleX() {
        return this._originalScale * (this.parentContainer.lossyScaleX ?? 1);
    }
    get lossyScaleY() {
        return this._originalScale * (this.parentContainer.lossyScaleY ?? 1);
    }
    get lossyScale() {
        return this.lossyScaleY;
    }

    get boundX() {
        return this.getLocalBounds().width * this.scale.x;
    }
    get boundY() {
        return this.getLocalBounds().height * this.scale.y;
    }
    get bounds() {
        return { width: this.boundX, height: this.boundY };
    }
    get boundWithOffsetX() {
        return this.boundX + this.getLocalBounds().x * this.scale.x;
    }
    get boundWithOffsetY() {
        return this.boundY + this.getLocalBounds().y * this.scale.y;
    }
    get boundsWithOffset() {
        return { width: this.boundWithOffsetX, height: this.boundWithOffsetY };
    }

    get externalBounds() {
        return this._externalBounds ?? (this.parentContainer !== undefined ?
            this.parentContainer.externalBounds : { width: 1, height: 1 });
    }
    set externalBounds(value) {
        this._externalBounds = value;
    }
    get unscaledExternalBounds() {
        return Vector2.multiplySize(this.externalBounds, this.scale.y / this.lossyScale);
    }

    scaleValue(value) {
        return value * this.lossyScale * Screen.main.stageScale;
    }

    onResolutionChanged() {
        if (this.parentContainer === undefined) return;
        
        this._onResolutionWillChangeCallback?.();

        this._useBounds = this.externalBounds;
        TransformHandler.updateGraphicsScale(this);

        this._refreshPosition();

        this._onResolutionChangedCallback?.();
    }

    onOrientationChanged() {
        this._onOrientationChangedCallback?.();
    }

    setTextureID(newTextureID) {
        if (this.texture !== undefined && this.texture.textureCacheIds[0] == newTextureID) return;
        let previousScale = { x: this.scale.x, y: this.scale.y };
        this.texture = AssetImporter.resourcesFind(newTextureID).texture;
        this.scale.set(1,1);
        this._originalSize.width = this.width;
        this._originalSize.height = this.height;
        this.scale.set(previousScale.x, previousScale.y);
        this._sizeRatio = this.width / this.height;
        this.onResolutionChanged();
    }

    setTexture(newTexture) {
        if (newTexture.texture !== undefined) {
            newTexture = newTexture.texture;
        }
        if (this.texture == newTexture) return;
        let previousScale = { x: this.scale.x, y: this.scale.y };
        this.texture = newTexture;
        this.scale.set(1,1);
        this._originalSize.width = this.width;
        this._originalSize.height = this.height;
        this.scale.set(previousScale.x, previousScale.y);
        this._sizeRatio = this.width / this.height;
        this.onResolutionChanged();
    }
}